import { useEffect, useState } from 'react';
import EditNavigation from '../../components/edit/navigation/EditNavigation';
import { Box } from '@mui/material';
import { PropertyField } from '../../system/field';
import {
  ListingResource,
  useFormProgress,
  useFormProgressInit,
  useFormFlags,
  useListingField,
  useListingStatus,
} from '@brightmls/nextgen.lae.flc';
import Edit from './Edit';
import { useNavigationLogic } from '../../components/edit/navigation/hooks/useNavigationLogic';
import { StepRenderer } from '../../components/edit/background/components/StepRenderer';
import Confetti from '../../components/edit/background/components/Confetti';
import ConfettiDebugFab from '../../components/edit/debug/ConfettiDebugFab';
import DebugSaveFab from '../../components/edit/debug/DebugSaveFab';
import { BackgroundGradient } from '../../components/edit/background/components/BackgroundGradient';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../flags';
import { useMetadata } from '../../contexts/MetadataContext';
import EditBackRoute from './EditBackRoute';
import { TOTAL_STEPS } from '../../components/edit/background/constants';
import { useDebugPreferences } from '../../hooks/useDebugPreferences';
import { useDebugInfo } from '../../contexts/DebugInfoContext';
import { useSidePanel } from '../../contexts/SidePanelContext';
import { DebugRequestPanel } from '../../components/common/DebugRequestPanel';
import { useMediaStore } from '@brightmls/nextgen.lae.mediacenter';
import { MediaProcessingIndicator } from '../../components/edit/media/MediaProcessingIndicator';

const EditContent = () => {
  // Use the new field-level hook for property type
  const propertyTypeField = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    'Residential',
  );
  const { template } = useMetadata();
  const { hasFeature } = useFeatureFlags();
  const showDebug = hasFeature(FeatureFlags.NGW_DEBUG_EDIT);
  const showConfetti = hasFeature(FeatureFlags.NGW_CONFETTI);
  const requireOfficeExclusiveDocument = hasFeature(
    FeatureFlags.NGW_OFFICE_EXCLUSIVE_DOC,
  );
  const hasSidepanelRedesign = hasFeature(
    FeatureFlags.NGW_EDIT_SIDEPANEL_REDESIGN,
  );
  const showAsyncProgress = hasFeature(
    FeatureFlags.NGW_MEDIA_V2_ASYNC_PROGRESS,
  );
  const isMediaHydrated = useMediaStore((state) => state.isHydrated);

  // Debug save FAB
  const { isDebugFlagEnabled, debugSaves } = useDebugPreferences();
  const { lastSaveDebug } = useDebugInfo();
  const { showPanel } = useSidePanel();

  const handleDebugFabClick = () => {
    if (lastSaveDebug) {
      showPanel(
        'Save Debug Information',
        <DebugRequestPanel debugInfo={lastSaveDebug} title="Last Save Debug" />,
        undefined,
        1000,
      );
    }
  };

  useFormProgressInit(template(propertyTypeField.value), TOTAL_STEPS);
  useFormFlags({
    requireOfficeExclusiveDocument,
  });

  const progressStats = useFormProgress();
  const navHeight = 160; // Shared navigation height
  const [isComplete, setIsComplete] = useState(
    progressStats.isComplete ?? false,
  );
  const [initialLoad, setInitialLoad] = useState(true);
  const [shouldFireConfetti, setShouldFireConfetti] = useState(false);

  useEffect(() => {
    // checking initial load prevents a false start before progressStats.isComplete is calculated
    // and potentially sets isComplete to true (preventing confetti firing on already completed listings)
    setTimeout(() => {
      if (
        !initialLoad &&
        // checking isComplete means we can only fire confetti once when
        // the listing is first completed, and not on subsequent refreshes
        !isComplete &&
        progressStats.isComplete
      ) {
        fireConfetti();
      } else {
        setInitialLoad(false);
      }
    }, 1000); // the timeout gives the agent time to finish their final input before confetti fires
  }, [isComplete, progressStats.isComplete]);

  const navigationLogicResult = useNavigationLogic();
  const { setActiveGroup, setActiveSection, isProgrammaticScroll } =
    navigationLogicResult;

  const handleActiveChange = (group: string, section: string) => {
    setActiveGroup(group);
    setActiveSection(section);
  };

  const isNavigatingAway =
    useListingStatus<boolean>('isNavigatingAway', false).value === true;

  const fireConfetti = () => {
    setIsComplete(true);
    setShouldFireConfetti(true);
    setTimeout(() => {
      setShouldFireConfetti(false);
    }, 3000); // 3000 for fireworks, 5000 for pride, 3000 for realistic
  };

  if (isNavigatingAway) {
    return <EditBackRoute />;
  }

  return (
    <>
      {showConfetti && <Confetti fireSignal={shouldFireConfetti} />}
      <Box
        sx={{
          position: 'relative',
          width: 1,
          minHeight: '100vh',
          ...(hasSidepanelRedesign
            ? {
                display: 'flex',
              }
            : {}),
        }}
      >
        <BackgroundGradient
          propertyType={propertyTypeField.value}
          navHeight={navHeight}
        />
        <Box
          sx={{
            display: 'flex',
            position: 'relative',
            zIndex: 1,
            maxWidth: '1400px',
          }}
        >
          <EditNavigation navigationLogic={navigationLogicResult} />
          <Edit
            propertyType={propertyTypeField.value}
            onActiveChange={handleActiveChange}
            isProgrammaticScroll={isProgrammaticScroll}
          />
        </Box>
        <StepRenderer
          propertyType={propertyTypeField.value}
          currentStep={progressStats.currentStep}
          isComplete={progressStats.isComplete}
          navHeight={navHeight}
        />
        {showConfetti && showDebug && (
          <ConfettiDebugFab onClick={fireConfetti} />
        )}
        {/* Debug Save FAB - only show when debug flag enabled and debugSaves is on */}
        {isDebugFlagEnabled && debugSaves && (
          <DebugSaveFab
            onClick={handleDebugFabClick}
            hasDebugInfo={!!lastSaveDebug}
          />
        )}
        {/* Floating media processing indicator */}
        {showAsyncProgress && isMediaHydrated && <MediaProcessingIndicator />}
      </Box>
    </>
  );
};

export default EditContent;
